<template>
  <section class="border-t border-default/60 bg-default">
    <UContainer class="py-12 sm:py-14">
      <h2
        v-if="title"
        class="text-center text-xl font-semibold text-highlighted"
      >
        {{ title }}
      </h2>
      <p
        v-if="subtitle"
        class="mx-auto mt-2 max-w-lg text-center text-sm text-muted"
      >
        {{ subtitle }}
      </p>
      <div
        v-if="!wheelId || builderPreview"
        class="mx-auto mt-8 flex size-56 items-center justify-center rounded-full border-8 border-primary/20 bg-primary/5 text-sm text-muted"
      >
        گردونه شانس
      </div>
      <div
        v-else
        class="mx-auto mt-8 max-w-sm space-y-4"
      >
        <div class="relative mx-auto size-56">
          <div class="absolute start-1/2 top-0 z-10 h-4 w-0 -translate-x-1/2 border-x-8 border-t-12 border-x-transparent border-t-primary" />
          <div
            class="size-full rounded-full border-8 border-primary/20 transition-transform duration-1000 ease-out"
            :style="{
              transform: `rotate(${rotation}deg)`,
              background: conic,
            }"
          />
        </div>
        <AuthPhoneInput v-model="phone" />
        <UButton
          block
          :loading="spinning"
          :disabled="!canSpin"
          @click="spin"
        >
          بچرخان
        </UButton>
        <p
          v-if="result"
          class="text-center text-sm font-medium text-primary"
        >
          {{ result }}
        </p>
        <p
          v-if="error"
          class="text-center text-sm text-error"
        >
          {{ error }}
        </p>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import AuthPhoneInput from '~/components/auth/PhoneInput.vue'
import { isCompleteIrMobile } from '~/utils/phone'
import { winningRotation } from '~/utils/campaign/spin'
import { apiErrorMessage } from '~/utils/apiError'

const props = defineProps<{
  title?: string
  subtitle?: string
  wheelId?: string
  builderPreview?: boolean
}>()

const phone = ref('')
const spinning = ref(false)
const rotation = ref(0)
const result = ref('')
const error = ref('')
const sectors = ref<Array<{ label: string; color: string }>>([])

const canSpin = computed(() => isCompleteIrMobile(phone.value) && !spinning.value)
const conic = computed(() => {
  if (!sectors.value.length) return 'conic-gradient(#2563eb, #0f766e, #c2410c, #64748b)'
  const slice = 100 / sectors.value.length
  return `conic-gradient(${sectors.value.map((sector, index) => `${sector.color} ${index * slice}% ${(index + 1) * slice}%`).join(', ')})`
})

onMounted(async () => {
  if (!props.wheelId || props.builderPreview) return
  try {
    const data = await $fetch<{ item: { sectors: Array<{ label: string; color: string }> } }>(`/api/campaign/spin/${props.wheelId}`)
    sectors.value = data.item.sectors
  }
  catch {
    sectors.value = []
  }
})

async function spin() {
  if (!props.wheelId || !canSpin.value) return
  spinning.value = true
  error.value = ''
  try {
    const data = await $fetch<{ item: { label: string; couponCode: string | null; type: string } }>(`/api/campaign/spin/${props.wheelId}/spin`, {
      method: 'POST',
      body: { phone: phone.value },
    })
    const index = Math.max(0, sectors.value.findIndex((sector) => sector.label === data.item.label))
    rotation.value = winningRotation(index, Math.max(1, sectors.value.length))
    result.value = data.item.couponCode
      ? `جایزه شما: ${data.item.label} — کد ${data.item.couponCode}`
      : `نتیجه: ${data.item.label}`
  }
  catch (err) {
    error.value = apiErrorMessage(err, 'چرخش انجام نشد')
  }
  finally {
    spinning.value = false
  }
}
</script>
